import React, { useState, useEffect } from 'react';
import { X, Upload, FileSpreadsheet, FileText, Play, AlertCircle, ChevronRight, ChevronLeft, Check, AlertTriangle, Info } from 'lucide-react';
import { workflowApi, fileApi, FileUpload, WorkflowDetails } from '../services/api';
import { getUserFriendlyErrorMessage } from '../utils/errorUtils';
import { FilePreview } from './FilePreview';
import { CodeDiffViewer } from './CodeDiffViewer';
import './WorkflowExecutionModal.css';

interface WorkflowExecutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
  sessionId: string;
  onExecutionStart: (executionId: string) => void;
}

interface FileMapping {
  role: string;
  description: string;
  required: boolean;
  file?: FileUpload;
}

interface ColumnMapping {
  original_column: string;
  new_column: string;
  confidence: number;
  transformation_needed: boolean;
  transformation_type?: string;
}

interface FileComparison {
  originalStructure: any;
  newStructure: any;
  matchedColumns: ColumnMapping[];
  unmatchedOriginal: string[];
  unmatchedNew: string[];
  structuralChanges: string[];
}

interface ScriptAdaptation {
  changes: any[];
  risk_level: string;
  requires_reuat: boolean;
  change_summary: string;
  diff?: string[];
}

enum WizardStep {
  FileUpload = 1,
  FileAnalysis = 2,
  ReviewChanges = 3,
  Execute = 4
}

export const WorkflowExecutionModal: React.FC<WorkflowExecutionModalProps> = ({
  isOpen,
  onClose,
  workflowId,
  workflowName,
  sessionId,
  onExecutionStart,
}) => {
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [workflowDetails, setWorkflowDetails] = useState<WorkflowDetails | null>(null);
  const [fileMappings, setFileMappings] = useState<FileMapping[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  
  // New states for multi-step wizard
  const [currentStep, setCurrentStep] = useState(WizardStep.FileUpload);
  const [analyzing, setAnalyzing] = useState(false);
  const [fileComparison, setFileComparison] = useState<FileComparison | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [scriptAdaptation, setScriptAdaptation] = useState<ScriptAdaptation | null>(null);
  const [skipAdaptation, setSkipAdaptation] = useState(false);
  const [userIntent, setUserIntent] = useState('');
  const [intentAdaptation, setIntentAdaptation] = useState<any>(null);
  const [showDetailedDiff, setShowDetailedDiff] = useState(false);
  const [showOriginalScript, setShowOriginalScript] = useState(false);
  const [adaptationProgress, setAdaptationProgress] = useState<string>('initializing');
  const [showAllChanges, setShowAllChanges] = useState(false);

  useEffect(() => {
    if (isOpen && workflowId) {
      fetchWorkflowDetails();
    }
  }, [isOpen, workflowId]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const fetchWorkflowDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await workflowApi.get(workflowId);
      setWorkflowDetails(response.data);
      
      // Extract file requirements from workflow content
      const fileMetadata = response.data.content?.input_file_metadata || {};
      const mappings: FileMapping[] = [];
      console.log('Workflow file metadata:', fileMetadata);
      
      // Parse file requirements from metadata
      if (fileMetadata.files && Array.isArray(fileMetadata.files)) {
        // Filter out incomplete entries first
        const validFiles = fileMetadata.files.filter((file: any) => {
          // Skip files that have no meaningful content (no filename and no columns)
          if (!file.filename && !file.columns && !file.sheets) {
            console.log('Skipping incomplete file entry in upload section:', file);
            return false;
          }
          return true;
        });
        
        // Track unique display names to avoid collisions
        const usedDisplayNames = new Set<string>();
        
        validFiles.forEach((fileInfo: any, index: number) => {
          const baseRole = fileInfo.role || 'Input File';
          let description = fileInfo.description || `${fileInfo.type || 'Data'} file for processing`;
          
          // Check if we need to disambiguate
          const filesWithSameRole = validFiles.filter((f: any) => 
            (f.role || 'Input File') === baseRole
          );
          
          let displayRole = baseRole;
          
          // Only disambiguate if there are multiple files with the same role
          if (filesWithSameRole.length > 1) {
            // Try multiple strategies to create a unique display name
            
            // Strategy 1: Use filename if available
            if (fileInfo.filename && fileInfo.filename.trim()) {
              const cleanFilename = fileInfo.filename.replace(/\.[^/.]+$/, '');
              
              // Truncate long filenames
              const truncatedName = cleanFilename.length > 25 
                ? cleanFilename.substring(0, 22) + '...' 
                : cleanFilename;
              
              displayRole = `${baseRole} - ${truncatedName}`;
              
              // Handle filename collisions (e.g., data.csv and data.xlsx)
              let uniqueCounter = 1;
              let uniqueDisplayRole = displayRole;
              while (usedDisplayNames.has(uniqueDisplayRole)) {
                uniqueCounter++;
                uniqueDisplayRole = `${displayRole} (${uniqueCounter})`;
              }
              displayRole = uniqueDisplayRole;
            }
            // Strategy 2: Use file type if different from default
            else if (fileInfo.type && fileInfo.type !== 'Data') {
              displayRole = `${baseRole} - ${fileInfo.type}`;
            }
            // Strategy 3: Use index as last resort
            else {
              const fileIndex = filesWithSameRole.findIndex((f: any) => f === fileInfo) + 1;
              displayRole = `${baseRole} ${fileIndex}`;
            }
          }
          
          // Track used names to prevent duplicates
          usedDisplayNames.add(displayRole);
          
          // Fix misleading file type descriptions
          if (description.toLowerCase().includes('xlsx file')) {
            description = description.replace(/xlsx file/gi, 'spreadsheet file (CSV or Excel)');
          }
          
          // Add original filename to description if it was used for disambiguation
          if (filesWithSameRole.length > 1 && fileInfo.filename && !description.includes(fileInfo.filename)) {
            description = `${description} (${fileInfo.filename})`;
          }
          
          mappings.push({
            role: displayRole,
            description: description,
            required: fileInfo.required !== false,
          });
        });
        
        // If no mappings were added, add a default one
        if (mappings.length === 0) {
          mappings.push({
            role: 'Input File',
            description: 'Spreadsheet file (CSV or Excel) for processing',
            required: true,
          });
        }
      } else {
        // Default to single file input if no specific requirements
        mappings.push({
          role: 'Input File',
          description: 'Data file for processing',
          required: true,
        });
      }
      
      setFileMappings(mappings);
    } catch (error) {
      console.error('Failed to fetch workflow details:', error);
      setError(getUserFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (index: number, file: File) => {
    const mappingRole = fileMappings[index].role;
    setUploadingFiles(prev => new Set(prev).add(mappingRole));
    
    try {
      const response = await fileApi.upload(file, sessionId);
      const updatedMappings = [...fileMappings];
      updatedMappings[index] = {
        ...updatedMappings[index],
        file: response.data,
      };
      setFileMappings(updatedMappings);
    } catch (error) {
      console.error('Failed to upload file:', error);
      setError(getUserFriendlyErrorMessage(error));
    } finally {
      setUploadingFiles(prev => {
        const next = new Set(prev);
        next.delete(mappingRole);
        return next;
      });
    }
  };

  const analyzeFiles = async () => {
    // For intent-based adaptation, we skip traditional file analysis
    // and go straight to the intent adaptation step
    setCurrentStep(WizardStep.FileAnalysis);
  };

  const updateColumnMapping = (index: number, newColumn: string) => {
    const updated = [...columnMappings];
    updated[index] = {
      ...updated[index],
      new_column: newColumn,
      confidence: newColumn === updated[index].original_column ? 1.0 : 0.8,
    };
    setColumnMappings(updated);
  };

  const previewAdaptation = async () => {
    if (skipAdaptation) {
      // Skip directly to execute step
      setCurrentStep(WizardStep.Execute);
      return;
    }
    
    setLoading(true);
    setError(null);
    setAdaptationProgress('analyzing');
    
    try {
      // Simulate progress stages
      setTimeout(() => setAdaptationProgress('understanding'), 500);
      
      // Prepare file mappings for API
      const inputFiles = fileMappings
        .filter(m => m.file)
        .map(m => ({
          file_id: m.file!.file_id,
          role: m.role,
        }));
      
      setTimeout(() => setAdaptationProgress('adapting'), 1000);
      
      // Use intent-based adaptation
      const response = await workflowApi.adaptByIntent(workflowId, {
        session_id: sessionId,
        input_files: inputFiles,
        user_intent: userIntent,
      });
      
      setAdaptationProgress('finalizing');
      setIntentAdaptation(response.data);
      setCurrentStep(WizardStep.ReviewChanges);
    } catch (error: any) {
      console.error('Failed to preview adaptation:', error);
      setError(getUserFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
      setAdaptationProgress('initializing');
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    setError(null);
    
    try {
      // Prepare file mappings for API
      const inputFiles = fileMappings
        .filter(m => m.file)
        .map(m => ({
          file_id: m.file!.file_id,
          role: m.role,
        }));

      const response = await workflowApi.executeAdapted(workflowId, {
        session_id: sessionId,
        input_files: inputFiles,
        column_mappings: undefined, // We're using intent-based adaptation now
        skip_adaptation: skipAdaptation,
        parameters: intentAdaptation ? {
          adapted_script: intentAdaptation.adapted_script,
          intent_prompt: userIntent,
          code_preservation_ratio: intentAdaptation.code_preservation_percentage,
          adaptation_changes: intentAdaptation.changes_preview?.map((c: any) => ({
            type: c.change_type,
            description: c.reason
          })),
          adapted_script_breakdown: intentAdaptation.adapted_script_breakdown
        } : undefined,
      });

      // Store execution context for the launchpad to pick up
      const executionContext = {
        sessionId: sessionId,
        executionId: response.data.execution_id,
        workflowId: workflowId,
        workflowName: workflowName,
        adaptedScript: intentAdaptation?.adapted_script || null,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('pendingWorkflowExecution', JSON.stringify(executionContext));
      
      onExecutionStart(response.data.execution_id);
      onClose();
    } catch (error: any) {
      console.error('Failed to execute workflow:', error);
      setError(getUserFriendlyErrorMessage(error));
    } finally {
      setExecuting(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType?.includes('spreadsheet') || fileType?.includes('excel') || fileType === 'text/csv') {
      return <FileSpreadsheet size={20} />;
    }
    return <FileText size={20} />;
  };

  const allRequiredFilesUploaded = fileMappings.filter(m => m.required).every(m => m.file);

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case WizardStep.FileUpload:
        return allRequiredFilesUploaded;
      case WizardStep.FileAnalysis:
        // For intent adaptation, require either user intent or skip adaptation
        return skipAdaptation || (userIntent && userIntent.trim().length > 0);
      case WizardStep.ReviewChanges:
        return true;
      default:
        return false;
    }
  };

  const handleNextStep = async () => {
    switch (currentStep) {
      case WizardStep.FileUpload:
        await analyzeFiles();
        break;
      case WizardStep.FileAnalysis:
        await previewAdaptation();
        break;
      case WizardStep.ReviewChanges:
        await handleExecute();
        break;
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > WizardStep.FileUpload) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case WizardStep.FileUpload:
        return 'Upload Files';
      case WizardStep.FileAnalysis:
        return 'Adapt Workflow';
      case WizardStep.ReviewChanges:
        return 'Review Changes';
      case WizardStep.Execute:
        return 'Executing Workflow';
      default:
        return '';
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return <AlertTriangle className="risk-icon high" size={16} />;
      case 'medium':
        return <AlertCircle className="risk-icon medium" size={16} />;
      default:
        return <Check className="risk-icon low" size={16} />;
    }
  };

  if (!isOpen) return null;

  const renderStepIndicator = () => {
    const steps = [
      { num: 1, label: 'Upload Files' },
      { num: 2, label: 'Adapt' },
      { num: 3, label: 'Review' }
    ];
    
    return (
      <div className="workflow-step-indicator">
        <div className="steps-container">
          {steps.map((step) => (
            <div
              key={`workflow-step-${step.num}`}
              className={`workflow-step ${currentStep >= step.num ? 'active' : ''} ${currentStep === step.num ? 'current' : ''}`}
            >
              <div className="workflow-step-number">{step.num}</div>
              <div className="workflow-step-label">{step.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFileUploadStep = () => (
    <>
      <div className="workflow-info">
        <h3>{workflowName}</h3>
        {workflowDetails?.workflow.description && (
          <p className="workflow-description">{workflowDetails.workflow.description}</p>
        )}
        {workflowDetails?.content?.instructions_summary && (
          <div className="instructions-summary">
            <h4>Workflow Summary</h4>
            <p>{workflowDetails.content.instructions_summary}</p>
          </div>
        )}
      </div>

      <FilePreview workflowId={workflowId} />

      <div className="file-mappings">
        <h4>Upload Your Files</h4>
        <p className="section-description">
          Upload the files required for this workflow:
        </p>

        {fileMappings.map((mapping, index) => (
          <div key={index} className="file-mapping-item">
            <div className="mapping-info">
              <h5>
                {mapping.role}
                {mapping.required && <span className="required-badge">Required</span>}
              </h5>
              <p>{mapping.description}</p>
            </div>
            
            {mapping.file ? (
              <div className="uploaded-file">
                {getFileIcon(mapping.file.file_type)}
                <span className="file-name">{mapping.file.filename}</span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const updatedMappings = [...fileMappings];
                    updatedMappings[index] = {
                      ...updatedMappings[index],
                      file: undefined,
                    };
                    setFileMappings(updatedMappings);
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="file-upload-area">
                <input
                  type="file"
                  id={`file-${index}`}
                  className="file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileUpload(index, file);
                    }
                  }}
                  accept=".csv,.xlsx,.xls,.pdf,.docx,.doc"
                  disabled={uploadingFiles.has(mapping.role)}
                />
                <label htmlFor={`file-${index}`} className="file-upload-label">
                  {uploadingFiles.has(mapping.role) ? (
                    <>
                      <div className="loading-spinner small"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      <span>Choose file</span>
                    </>
                  )}
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );

  const renderIntentAdaptationStep = () => (
    <div className="intent-adaptation-step">
      <h4>Adapt Workflow to Your Needs</h4>
      <p className="section-description">
        Tell us what you want to do with your new file, and Assertion will adapt the tested workflow intelligently.
      </p>

      <div className="original-intent-box">
        <div className="intent-box-header">
          <h5>Original workflow purpose:</h5>
          <button 
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setShowOriginalScript(true)}
          >
            View Script & Process Flow
          </button>
        </div>
        <p className="original-intent">
          {workflowDetails?.content?.instructions_summary || 'Process and transform accounting data'}
        </p>
      </div>

      <div className="new-intent-section">
        <label htmlFor="user-intent">What would you like to do with your new file?</label>
        <textarea
          id="user-intent"
          className="intent-input"
          rows={4}
          placeholder="Describe how you want to modify this workflow for your specific needs..."
          value={userIntent}
          onChange={(e) => setUserIntent(e.target.value)}
        />
        <div className="intent-examples">
          <p className="examples-label">Examples:</p>
          <ul>
            <li>"Same analysis but exclude product dimension"</li>
            <li>"Only show Q4 data instead of full year"</li>
            <li>"Add tax calculations to the existing report"</li>
          </ul>
        </div>
      </div>

      <div className="ai-hint">
        <Info size={20} />
        <p>
          Assertion will adapt the tested workflow while preserving as much validated code as possible.
          This ensures reliability while meeting your new requirements.
        </p>
      </div>

      <div className="adaptation-option">
        <label>
          <input
            type="checkbox"
            checked={skipAdaptation}
            onChange={(e) => setSkipAdaptation(e.target.checked)}
          />
          <span>Skip adaptation and use the original workflow as-is</span>
        </label>
      </div>

      {/* Script Preview Modal */}
      {showOriginalScript && workflowDetails?.content && (
        <div className="script-preview-modal" onClick={() => setShowOriginalScript(false)}>
          <div className="script-preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="script-preview-header">
              <h3>Original Workflow Script & Process Flow</h3>
              <button className="close-button" onClick={() => setShowOriginalScript(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="script-preview-body">
              {workflowDetails.content.script_breakdown && (
                <div className="script-summary">
                  <h4>Process Flow Summary:</h4>
                  <p>{workflowDetails.content.script_breakdown.summary}</p>
                  {workflowDetails.content.script_breakdown.steps && workflowDetails.content.script_breakdown.steps.length > 0 && (
                    <ol className="process-steps">
                      {workflowDetails.content.script_breakdown.steps.map((step: any, idx: number) => (
                        <li key={idx}>
                          <strong>{step.title}</strong>
                          {step.description && <p>{step.description}</p>}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
              {workflowDetails.content.scripts?.[0]?.code && (
                <div className="script-code">
                  <h4>Python Script:</h4>
                  <pre className="code-block">
                    <code>{workflowDetails.content.scripts[0].code}</code>
                  </pre>
                </div>
              )}
              {!workflowDetails.content.script_breakdown && !workflowDetails.content.scripts?.[0]?.code && (
                <div className="no-content-message">
                  <p>No process flow or script information available for this workflow.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderReviewChangesStep = () => (
    <div className="review-changes-step">
      <h4>Review Workflow Adaptation</h4>
      
      {intentAdaptation && (
        <>
          <div className="adaptation-summary">
            <div className="preservation-metric">
              <Check size={20} className="preservation-icon" />
              <span className="preservation-label">
                <strong>{intentAdaptation.code_preservation_percentage}%</strong> of validated code preserved
              </span>
            </div>
            
            <div className="risk-assessment">
              {getRiskIcon(intentAdaptation.risk_assessment.overall_risk)}
              <span className="risk-label">
                Risk Level: <strong>{intentAdaptation.risk_assessment.overall_risk}</strong>
              </span>
            </div>
            
            <p className="recommendation">{intentAdaptation.risk_assessment.recommendation}</p>
            
            {intentAdaptation.risk_assessment.requires_reuat && (
              <div className="reuat-warning">
                <AlertTriangle size={20} />
                <span>These changes require re-running UAT testing</span>
              </div>
            )}
          </div>

          <div className="intent-comparison">
            <div className="intent-box">
              <h5>Original Intent</h5>
              <p>{intentAdaptation.original_intent}</p>
            </div>
            <div className="intent-arrow">→</div>
            <div className="intent-box">
              <h5>New Intent</h5>
              <p>{intentAdaptation.new_intent}</p>
            </div>
          </div>

          {intentAdaptation.risk_assessment.risk_factors.length > 0 && (
            <div className="risk-factors">
              <h5>Risk Factors:</h5>
              <ul>
                {intentAdaptation.risk_assessment.risk_factors.map((factor: string, idx: number) => (
                  <li key={idx}>{factor}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="script-changes">
            <h5>Code Changes ({intentAdaptation.changes_preview.length})</h5>
            <div className="changes-list">
              {intentAdaptation.changes_preview.slice(0, showAllChanges ? undefined : 5).map((change: any, idx: number) => (
                <div key={idx} className="change-item">
                  <div className="change-header">
                    <span className="line-range">
                      Lines {change.line_start}-{change.line_end}
                    </span>
                    <span className={`change-type ${change.risk_level}`}>
                      {change.change_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="change-reason">{change.reason}</div>
                  <div className="code-diff">
                    <div className="original-code">
                      <span className="diff-label">- </span>
                      <code>{change.original_code}</code>
                    </div>
                    <div className="adapted-code">
                      <span className="diff-label">+ </span>
                      <code>{change.adapted_code}</code>
                    </div>
                  </div>
                </div>
              ))}
              {intentAdaptation.changes_preview.length > 5 && (
                <button 
                  className="more-changes-button"
                  onClick={() => setShowAllChanges(!showAllChanges)}
                >
                  {showAllChanges 
                    ? 'Show fewer changes' 
                    : `... and ${intentAdaptation.changes_preview.length - 5} more changes`
                  }
                </button>
              )}
            </div>
          </div>

          <div className="diff-viewer-toggle">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowDetailedDiff(!showDetailedDiff)}
            >
              {showDetailedDiff ? 'Hide' : 'Show'} Detailed Code Diff
            </button>
          </div>

          {showDetailedDiff && intentAdaptation.adapted_script && (
            <div className="detailed-diff-container">
              <CodeDiffViewer
                originalCode={workflowDetails?.content?.scripts?.[0]?.code || ''}
                adaptedCode={intentAdaptation.adapted_script}
                changes={intentAdaptation.changes_preview}
                preservationRatio={intentAdaptation.code_preservation_percentage}
                visualDiff={intentAdaptation.visual_diff}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderExecuteStep = () => (
    <div className="execute-step">
      <h4>Ready to Execute</h4>
      <p className="section-description">
        Your workflow is ready to be executed with the uploaded files.
      </p>
      
      <div className="execution-summary">
        <h5>Execution Summary:</h5>
        <ul>
          <li>Workflow: {workflowName}</li>
          <li>Files uploaded: {fileMappings.filter(m => m.file).length}</li>
          {skipAdaptation && (
            <li className="warning-item">
              <AlertCircle size={16} />
              Running without file adaptation (workflow has no original file structure)
            </li>
          )}
        </ul>
      </div>
      
      <div className="execution-notice">
        <Info size={20} />
        <p>
          Click Execute below to run the workflow. The execution will process your uploaded files
          and generate the output according to the workflow's logic.
        </p>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container workflow-execution-wizard" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{getStepTitle()}</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {renderStepIndicator()}

        <div className="modal-content wizard-content">
          {loading || analyzing ? (
            <div className="loading-state enhanced">
              <div className="loading-spinner"></div>
              <h3>Adapting Your Workflow</h3>
              <div className="progress-steps">
                <div className={`progress-step ${adaptationProgress === 'analyzing' || adaptationProgress === 'understanding' || adaptationProgress === 'adapting' || adaptationProgress === 'finalizing' ? 'active' : ''} ${adaptationProgress !== 'initializing' && adaptationProgress !== 'analyzing' ? 'completed' : ''}`}>
                  <div className="step-icon">
                    {adaptationProgress !== 'initializing' && adaptationProgress !== 'analyzing' ? <Check size={16} /> : '1'}
                  </div>
                  <div className="step-text">Analyzing your requirements</div>
                </div>
                <div className={`progress-step ${adaptationProgress === 'understanding' || adaptationProgress === 'adapting' || adaptationProgress === 'finalizing' ? 'active' : ''} ${adaptationProgress === 'adapting' || adaptationProgress === 'finalizing' ? 'completed' : ''}`}>
                  <div className="step-icon">
                    {adaptationProgress === 'adapting' || adaptationProgress === 'finalizing' ? <Check size={16} /> : '2'}
                  </div>
                  <div className="step-text">Understanding workflow context</div>
                </div>
                <div className={`progress-step ${adaptationProgress === 'adapting' || adaptationProgress === 'finalizing' ? 'active' : ''} ${adaptationProgress === 'finalizing' ? 'completed' : ''}`}>
                  <div className="step-icon">
                    {adaptationProgress === 'finalizing' ? <Check size={16} /> : '3'}
                  </div>
                  <div className="step-text">Adapting code intelligently</div>
                </div>
                <div className={`progress-step ${adaptationProgress === 'finalizing' ? 'active' : ''}`}>
                  <div className="step-icon">4</div>
                  <div className="step-text">Finalizing adaptation</div>
                </div>
              </div>
              <p className="loading-message">
                {adaptationProgress === 'analyzing' && 'Reading your requirements and workflow intent...'}
                {adaptationProgress === 'understanding' && 'Analyzing the original workflow structure...'}
                {adaptationProgress === 'adapting' && ''}
                {adaptationProgress === 'finalizing' && 'Preparing your adapted workflow for review...'}
                {adaptationProgress === 'initializing' && 'Starting adaptation process...'}
              </p>
            </div>
          ) : (currentStep === WizardStep.Execute) ? (
            <div className="executing-state">
              <div className="loading-spinner large"></div>
              <h3>Executing Workflow</h3>
              <p>Your workflow is being executed with the adapted script...</p>
            </div>
          ) : (
            <>
              {currentStep === WizardStep.FileUpload && renderFileUploadStep()}
              {currentStep === WizardStep.FileAnalysis && renderIntentAdaptationStep()}
              {currentStep === WizardStep.ReviewChanges && renderReviewChangesStep()}
              {(currentStep as WizardStep) === WizardStep.Execute && renderExecuteStep()}

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <div>
            {(currentStep > WizardStep.FileUpload && currentStep < WizardStep.Execute) && (
              <button className="btn btn-secondary" onClick={handlePreviousStep}>
                <ChevronLeft size={16} />
                Previous
              </button>
            )}
          </div>
          
          <div className="actions-right">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            
            {(currentStep < WizardStep.Execute) && (
              <button
                className="btn btn-primary"
                onClick={handleNextStep}
                disabled={
                  loading || 
                  analyzing || 
                  executing || 
                  !canProceedToNextStep() || 
                  uploadingFiles.size > 0
                }
              >
                {currentStep === WizardStep.ReviewChanges ? (
                  <>
                    <Play size={16} />
                    <span>Execute</span>
                  </>
                ) : (
                  <>
                    <span>Next</span>
                    <ChevronRight size={16} />
                  </>
                )}
              </button>
            )}
            
            {(currentStep as WizardStep) === WizardStep.Execute && (
              <button
                className="btn btn-primary"
                onClick={handleExecute}
                disabled={executing}
              >
                {executing ? (
                  <>
                    <div className="loading-spinner small inline"></div>
                    <span>Executing...</span>
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    <span>Execute</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};